import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Phone, Shield } from 'lucide-react';
import colors from '../config/colors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthenticationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('mobile'); // 'mobile' or 'otp'
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOtp, setTestOtp] = useState(''); // Store OTP for display

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate mobile number (Malaysian format)
    const cleanNumber = mobileNumber.replace(/\s+/g, '');
    if (!/^(\+60|60|0)[0-9]{9,10}$/.test(cleanNumber)) {
      setError('Please enter a valid Malaysian mobile number');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/request-otp`, {
        mobile_number: cleanNumber
      });

      if (response.data.success) {
        setOtpSent(true);
        setStep('otp');
        // Store OTP for display (for testing/demo purposes)
        if (response.data.otp_for_testing) {
          setTestOtp(response.data.otp_for_testing);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-otp`, {
        mobile_number: mobileNumber.replace(/\s+/g, ''),
        otp_code: otp
      });

      if (response.data.success) {
        // Store user ID in session storage
        sessionStorage.setItem('user_id', response.data.user_id);
        sessionStorage.setItem('mobile_number', response.data.mobile_number);
        
        // Navigate to questionnaire
        navigate('/questionnaire');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp('');
    setError('');
    setTestOtp(''); // Clear displayed OTP
    await handleRequestOTP({ preventDefault: () => {} });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bgPage,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: '900',
            color: colors.textPrimary,
            marginBottom: '16px',
            fontFamily: "'PP Right Grotesk', 'Inter', Arial, sans-serif",
            textTransform: 'uppercase'
          }}>
            LET'S GET STARTED
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: colors.textSecondary,
            lineHeight: '1.6'
          }}>
            We'll send you a quick verification code to get started
          </p>
        </div>

        {/* Form Card */}
        <div style={{
          background: colors.bgCard,
          padding: '48px',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)'
        }}>
          
          {step === 'mobile' ? (
            // Mobile Number Step
            <form onSubmit={handleRequestOTP}>
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: colors.textPrimary,
                  marginBottom: '12px'
                }}>
                  <Phone size={18} color={colors.brandPrimary} />
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="+60 12 345 6789"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '1.1rem',
                    border: `2px solid ${colors.borderMedium}`,
                    borderRadius: '8px',
                    background: colors.bgPage,
                    color: colors.textPrimary,
                    outline: 'none',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.brandPrimary}
                  onBlur={(e) => e.target.style.borderColor = colors.borderMedium}
                  required
                />
                <p style={{
                  fontSize: '0.85rem',
                  color: colors.textMuted,
                  marginTop: '8px'
                }}>
                  We'll send a 6-digit code via SMS
                </p>
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: '#FEE2E2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  color: '#991B1B',
                  fontSize: '0.9rem',
                  marginBottom: '24px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: colors.brandPrimary,
                  color: colors.textInverse,
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '16px 32px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseOver={(e) => !loading && (e.target.style.transform = 'scale(1.02)')}
                onMouseOut={(e) => !loading && (e.target.style.transform = 'scale(1)')}
              >
                {loading ? 'SENDING...' : 'SEND CODE'}
                {!loading && <ArrowRight size={20} />}
              </button>
            </form>
          ) : (
            // OTP Verification Step
            <form onSubmit={handleVerifyOTP}>
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: colors.textPrimary,
                  marginBottom: '12px'
                }}>
                  <Shield size={18} color={colors.brandPrimary} />
                  Enter 6-Digit Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '2rem',
                    letterSpacing: '0.5em',
                    textAlign: 'center',
                    fontWeight: '700',
                    border: `2px solid ${colors.borderMedium}`,
                    borderRadius: '8px',
                    background: colors.bgPage,
                    color: colors.textPrimary,
                    outline: 'none',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.brandPrimary}
                  onBlur={(e) => e.target.style.borderColor = colors.borderMedium}
                  required
                  autoFocus
                />
                <p style={{
                  fontSize: '0.85rem',
                  color: colors.textMuted,
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  Sent to {mobileNumber}
                </p>
                
                {/* Display test OTP for demo/testing purposes */}
                {testOtp && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: '#FEF3C7',
                    border: '2px solid #F59E0B',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#92400E',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📱 Test OTP Code
                    </p>
                    <p style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#B45309',
                      letterSpacing: '0.3em',
                      margin: 0
                    }}>
                      {testOtp}
                    </p>
                    <p style={{
                      fontSize: '0.7rem',
                      color: '#92400E',
                      marginTop: '4px',
                      marginBottom: 0
                    }}>
                      (For testing only - no real SMS sent)
                    </p>
                  </div>
                )}
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: '#FEE2E2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  color: '#991B1B',
                  fontSize: '0.9rem',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                style={{
                  width: '100%',
                  background: colors.brandPrimary,
                  color: colors.textInverse,
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '16px 32px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: (loading || otp.length !== 6) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  opacity: (loading || otp.length !== 6) ? 0.6 : 1,
                  marginBottom: '16px'
                }}
                onMouseOver={(e) => !loading && otp.length === 6 && (e.target.style.transform = 'scale(1.02)')}
                onMouseOut={(e) => !loading && (e.target.style.transform = 'scale(1)')}
              >
                {loading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                {!loading && <ArrowRight size={20} />}
              </button>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={loading}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: colors.brandPrimary,
                  border: `2px solid ${colors.brandPrimary}`,
                  borderRadius: '10rem',
                  padding: '12px 24px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => !loading && (e.target.style.background = colors.bgAccent)}
                onMouseOut={(e) => (e.target.style.background = 'transparent')}
              >
                Didn't receive code? Resend
              </button>
            </form>
          )}
        </div>

        {/* Security Note */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px',
          padding: '16px',
          background: colors.bgAccent,
          borderRadius: '8px'
        }}>
          <p style={{
            fontSize: '0.85rem',
            color: colors.textSecondary,
            margin: 0
          }}>
            🔒 Your information is secure and will only be used for your school diagnostic report
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationPage;
